import { z } from 'zod';
import {
  Product360View,
  ProductListRow,
  QueryMetadata,
  pagedRows,
  type GlobalFilters,
  type PagedRows,
  type QueryResult,
} from '@aurora-pvi/contracts';
import { apiFetch } from './apiFetch';
import { filterParams } from './filterParams';

/**
 * The `products` group of `Repositories`, backed by the real API
 * (`GET /products` and `GET /products/:productId` in apps/api).
 *
 * Same posture as `httpPortfolioRepository.ts` throughout: parsed through the
 * real schema, not cast; routed only for the default (unfiltered,
 * current-period) view, since neither route accepts `GlobalFilters` yet.
 *
 * `get360` is real now, not a fixture fallback: the product ids the real list
 * returns are the same v5 UUIDs the fixture generates from the same seed, so
 * this always resolves the product the list actually showed. Its
 * `availableTabs` is deliberately short — see apps/api/src/routes/products.ts
 * for exactly which tabs this tenant's loaded data can back.
 */

const ListEnvelope = z.object({ data: pagedRows(ProductListRow), meta: QueryMetadata });
const DetailEnvelope = z.object({ data: Product360View, meta: QueryMetadata });

export function createHttpProductRepository(baseUrl: string) {
  return {
    async list(filters: GlobalFilters): Promise<QueryResult<PagedRows<ProductListRow>>> {
      const response = await apiFetch(`${baseUrl}/products?${filterParams(filters)}`);
      if (!response.ok) {
        throw new Error(`GET /products failed: ${response.status} ${response.statusText}`);
      }
      const json: unknown = await response.json();
      return ListEnvelope.parse(json);
    },

    async get360(productId: string, filters: GlobalFilters): Promise<QueryResult<Product360View>> {
      const response = await apiFetch(`${baseUrl}/products/${encodeURIComponent(productId)}?${filterParams(filters)}`);
      if (!response.ok) {
        throw new Error(
          `GET /products/${productId} failed: ${response.status} ${response.statusText}`,
        );
      }
      const json: unknown = await response.json();
      return DetailEnvelope.parse(json);
    },
  };
}
